/**
 * Requester sign-in — Step 10 (magic links), Step 171 (Microsoft first).
 *
 * A SEPARATE door from /login (the agent door): digitaladmin's three-domains
 * rule — sending an expired requester at the staff login would push them at a
 * Microsoft tenant they may have no agent account in. Bare fetch on purpose
 * throughout: this is the portal auth domain, where a 401/generic answer IS
 * the flow, and apiFetch's agent-login redirect would be exactly the bug.
 *
 * Kai, Step 171: *"the portal should default to ms365, but have the ability
 * via a separate url to not — all our customers are internal, and should all
 * have a ms account."* So Microsoft is the front door when it is configured,
 * and the emailed link lives at `/portal/login/email` for the exceptions: a
 * contractor, a personal address, or somebody locked out of the very account
 * they are ringing about. The link to it is present but quiet.
 *
 * When Microsoft is NOT configured, this page IS the email form. A front door
 * that goes nowhere is worse than no front door.
 */
import { useEffect, useState } from 'react';
import { useUrlState } from '../../lib/urlState';
import { LifeBuoy, MailCheck, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

export function PortalLoginPage({ emailOnly = false }: { emailOnly?: boolean }) {
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [microsoft, setMicrosoft] = useState<boolean | null>(null);
  const [emailOnMain, setEmailOnMain] = useState(false);
  // The project's reader rather than a one-off parse of window.location: both
  // stay in sync, which matters because the Microsoft door bounces back here
  // with ?ssoError= and the page must show it without a reload.
  const [ssoError] = useUrlState('ssoError', null);
  const [returnTo] = useUrlState('returnTo', null);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/tenant/branding')
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        setTenantName(b?.name ?? null);
        setMicrosoft(!!b?.portalMicrosoftSignIn);
        setEmailOnMain(!!b?.portalEmailLinkOnMainPage);
      })
      // Unreachable branding must not strand somebody: fall back to the door
      // that needs no configuration.
      .catch(() => setMicrosoft(false));
  }, []);

  async function requestLink() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/auth/portal/request-link', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) setError(body?.error ?? 'Something went wrong — try again.');
      else {
        setSent(body?.message ?? 'Check your email.');
        if (body?.devLink) setDevLink(body.devLink); // dev builds only — see portal-auth.ts
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
            <LifeBuoy className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">{tenantName ?? 'Support'}</p>
            <p className="text-xs text-muted-foreground">Requester sign in</p>
          </div>
        </div>
        {ssoError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {ssoError}
          </div>
        )}

        {/* Still asking the server which door this is. Rendering either one
            first and correcting it is a flash of the wrong sign-in. */}
        {microsoft === null && <div className="h-40 animate-pulse rounded-lg border bg-muted/40" />}

        {microsoft && !emailOnly && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Sign in</CardTitle>
              <CardDescription>Use the Microsoft account you sign in to work with.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                onClick={() => {
                  // A full navigation on purpose: this leaves the app for
                  // Microsoft, so the router must not try to handle it.
                  window.location.href =
                    '/auth/sso/login?audience=portal' +
                    (returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : '');
                }}
              >
                Continue with Microsoft
              </Button>
            </CardContent>
          </Card>
        )}

        {(microsoft === false || emailOnly || emailOnMain) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Sign in by email</CardTitle>
              <CardDescription>
                We'll send a link that signs you in — no password to remember.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {sent ? (
                <>
                  <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
                    <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <p className="text-sm">{sent}</p>
                  </div>
                  {devLink && (
                    <a
                      href={devLink}
                      className="block rounded-md border border-dashed border-brand/50 bg-brand/5 p-3 text-center text-xs text-brand hover:underline"
                    >
                      Development only: mail isn't configured, so here's your sign-in link →
                    </a>
                  )}
                </>
              ) : (
                <>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    aria-label="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && email.trim() && requestLink()}
                    autoFocus
                  />
                  {error && <p className="text-xs text-destructive">{error}</p>}
                  <Button className="w-full" onClick={requestLink} disabled={busy || !email.trim()}>
                    {busy ? 'Sending…' : 'Email me a sign-in link'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* The quiet way past the front door. Present, findable, and not
            competing with it — the people who need it are the exception. */}
        {microsoft && !emailOnly && !emailOnMain && (
          <p className="text-center text-xs text-muted-foreground">
            <a className="inline-flex items-center gap-1 hover:text-foreground" href="/portal/login/email">
              <Mail className="h-3.5 w-3.5" />
              No Microsoft account? Sign in by email instead
            </a>
          </p>
        )}
        {emailOnly && microsoft && (
          <p className="text-center text-xs text-muted-foreground">
            <a className="hover:text-foreground" href="/portal/login">
              Back to signing in with Microsoft
            </a>
          </p>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Work here?{' '}
          <a className="text-brand hover:underline" href="/login">
            Agent sign in
          </a>
        </p>
      </div>
    </div>
  );
}
